import { useLogtoRoles } from '@/hooks/useLogtoRoles';

/**
 * Privileged Logto roles defined under `admin-account.qlash.gg → Ruoli`.
 * Add new role names here as they get provisioned in the PROD tenant —
 * TypeScript will then enforce only-valid-role usage at call sites.
 *
 * - `administrator` — full QLASH admin; passes <AdminGate> on the DEV build
 * - `beta` — beta tester; passes <AdminGate> AND unlocks beta-flagged
 *   features inside the public PROD build
 */
export type PrivilegedRole = 'administrator' | 'beta';

/**
 * Returns `true` when the currently signed-in user carries the given Logto
 * role in their access-token `roles[]` claim. Used to gate feature surfaces
 * by role (independent of the build-level <AdminGate>).
 *
 * Reactivity guarantees:
 *  - Recomputes when `bridgeAuthenticated` flips (sign-in / sign-out clears
 *    or replaces the stored access token).
 *  - Recomputes when the requested `role` changes (so a component can swap
 *    which role it gates against without remount).
 *  - Recomputes when the TOKEN changes — see below.
 *
 * 🔴 **Il terzo punto era assente, ed era dichiarato come una scelta accettabile.**
 * Fino al 2026-09-06 qui c'era scritto: «Does NOT recompute on token refresh mid-session —
 * Acceptable trade-off: roles change rarely and a stale read just defers the new privilege
 * by one session boundary». Misurato da or45 su `stg-tma`: un utente a cui viene assegnato
 * `beta` apre la sezione Limulo e vede una schermata **VUOTA**, pur avendo gia' nel token
 * `["user","tester","beta"]`; rende solo al caricamento successivo. Il differimento non era
 * «di una sessione»: era di un caricamento, e nel frattempo lo schermo non diceva niente.
 * 📌 Il dato aggiornato c'era gia' — `getLogtoRolesFromAccessToken()` rilegge `localStorage`
 * a ogni chiamata: a bloccarlo era **solo il memo**.
 *
 * ⚠️ **La rivalutazione e' SIMMETRICA, e va detto.** Il gate riflette il token corrente:
 * se un token rinnovato non portasse piu' il ruolo, la sezione sparirebbe *sotto* l'utente
 * invece che al caricamento successivo. E' il comportamento corretto — un gate che
 * ricordasse un privilegio revocato sarebbe peggio — ed e' meno brutale di prima, perche'
 * al posto del vuoto compare il pannello di `<RoleGate>`.
 *
 * Returns `false` for unauthenticated users and on JWT decode failures.
 *
 * Usage:
 * ```ts
 * const isBeta = useHasLogtoRole('beta');
 * if (isBeta) return <BetaFeature />;
 * ```
 *
 * For wrapping entire subtrees / route elements, prefer <RoleGate>.
 */
export function useHasLogtoRole(role: PrivilegedRole): boolean {
  // ⚠️ La sottoscrizione alla rivalutazione sta in `useLogtoRoles`, e QUESTO hook e' un
  // involucro sottile. Non e' pigrizia: `AdminGate` chiede «almeno uno fra due ruoli», e
  // rispondergli qui avrebbe voluto dire o due sottoscrizioni per una domanda sola, o una
  // seconda copia della logica di rivalutazione. Due copie divergono, e la seconda diverge
  // in silenzio.
  return useLogtoRoles().includes(role);
}
